import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';

import { useGetPriorities } from 'security-app/hooks/useInvestigationsAPI';
import useUsersAndTeams from 'security-app/components/Investigations/hooks/useUsersAndTeams';
import { Icon } from 'components/common';
import { Label } from 'components/bootstrap';

const ICON_TYPES = {
  team: 'group',
  user: 'person',
} as const;

const Container = styled.div`
  display: flex;
  gap: 16px;
`;

const MainDetailContainer = styled.div`
  display: flex;
  gap: 8px;
  line-height: 1;
`;

const DetailLabel = styled(Label)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-weight: bold;
`;

const AssigneeIcon = styled(Icon)`
  width: 15px;
  margin-right: 2px;
  vertical-align: middle;
`;

const MainDetail = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <MainDetailContainer>
    {label} <DetailLabel bsStyle="default">{value ?? 'none'}</DetailLabel>
  </MainDetailContainer>
);

const Priority = ({ priority }: { priority: number }) => {
  const { priorities } = useGetPriorities(true);
  const priorityText = useMemo(
    () => priorities.find((prio) => prio.priority === priority)?.text,
    [priorities, priority],
  );

  return <MainDetail label="Priority" value={priorityText} />;
};

const AssignedTo = ({ assignedTo }: { assignedTo: string }) => {
  const usersAndTeams = useUsersAndTeams();
  const assignee = usersAndTeams[assignedTo]?.label;

  return (
    <MainDetail
      label="Assigned to"
      value={
        <>
          {assignee && <AssigneeIcon name={ICON_TYPES[usersAndTeams[assignedTo]?.type]} size="xs" />}
          {assignee ?? 'unassigned'}
        </>
      }
    />
  );
};

type Props = {
  priority: number;
  status: string;
  assignedTo: string;
};

const MainDetails = ({ priority, status, assignedTo }: Props) => (
  <Container>
    <Priority priority={priority} />
    <MainDetail label="Status" value={status} />
    <AssignedTo assignedTo={assignedTo} />
  </Container>
);
export default MainDetails;
